import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  RefreshIcon,
  LayersIcon,
  AlertIcon,
  CheckCircleIcon,
} from '../components/common/Icons';
import TemplateCard from '../components/templates/TemplateCard';
import NavTreePreview from '../components/templates/NavTreePreview';
import type { NavTreeGroup } from '../components/templates/NavTreePreview';
import type { TemplateCardTemplate } from '../components/templates/TemplateCard';

interface TemplateDetail extends TemplateCardTemplate {
  feature_defaults: string[];
  ai_presets: Record<string, unknown>;
  branding_defaults: Record<string, unknown>;
  stub_metadata: Record<string, { title?: string; expectedPhase?: string }>;
  view_registry: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

const TemplatesPage: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();

  // State
  const [templates, setTemplates] = useState<TemplateCardTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // View mode
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [templateDetail, setTemplateDetail] = useState<TemplateDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit state (for non-system templates)
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<{
    name: string;
    description: string;
    version: string;
    is_active: boolean;
  }>({ name: '', description: '', version: '', is_active: true });
  const [isSaving, setIsSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch('/api/admin/templates?active_only=false');
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to fetch templates');
      }
    } catch (err) {
      setError('Failed to connect to API');
    }
    setLoading(false);
  }, [authFetch]);

  const fetchTemplateDetail = useCallback(async (slug: string) => {
    setDetailLoading(true);
    setError(null);
    try {
      const response = await authFetch(`/api/admin/templates/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setTemplateDetail(data);
        // Initialize edit data
        setEditData({
          name: data.name,
          description: data.description || '',
          version: data.version,
          is_active: data.is_active,
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to fetch template details');
      }
    } catch (err) {
      setError('Failed to connect to API');
    }
    setDetailLoading(false);
  }, [authFetch]);

  useEffect(() => {
    if (authLoading) return;
    fetchTemplates();
  }, [authLoading, fetchTemplates]);

  const handleSelectTemplate = (slug: string) => {
    setSelectedSlug(slug);
    setViewMode('detail');
    setIsEditing(false);
    fetchTemplateDetail(slug);
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedSlug(null);
    setTemplateDetail(null);
    setIsEditing(false);
    // Refresh list to pick up any updates
    fetchTemplates();
  };

  const handleSaveEdit = async () => {
    if (!templateDetail || !selectedSlug) return;

    setIsSaving(true);
    setError(null);
    try {
      const response = await authFetch(`/api/admin/templates/${selectedSlug}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editData.name,
          description: editData.description || null,
          version: editData.version,
        }),
      });

      if (response.ok) {
        const updated = await response.json();
        setTemplateDetail(updated);
        setIsEditing(false);
        setSuccessMessage('Template updated successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to update template');
      }
    } catch (err) {
      setError('Failed to connect to API');
    }
    setIsSaving(false);
  };

  const renderListView = () => (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Schema Templates</h1>
          <p className="page-subtitle">
            {templates.length} template{templates.length !== 1 ? 's' : ''} available
          </p>
        </div>
        <div className="page-header-right">
          <button className="btn btn-ghost" onClick={fetchTemplates}>
            <RefreshIcon size={18} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="glass-card">
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading templates...</p>
          </div>
        </div>
      ) : templates.length === 0 ? (
        <div className="glass-card">
          <div className="empty-state">
            <LayersIcon size={48} />
            <p>No schema templates found</p>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              Create a template to get started.
            </span>
          </div>
        </div>
      ) : (
        <div
          data-testid="templates-grid"
          style={{
            display: 'grid',
            gap: '1rem',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          }}
        >
          {templates.map(template => (
            <TemplateCard
              key={template.slug}
              template={template}
              onSelect={handleSelectTemplate}
            />
          ))}
        </div>
      )}
    </>
  );

  const renderDetailView = () => {
    if (detailLoading || !templateDetail) {
      return (
        <div className="glass-card">
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading template details...</p>
          </div>
        </div>
      );
    }

    const navGroups = (templateDetail.navigation?.groups || []) as unknown as NavTreeGroup[];

    return (
      <>
        {/* Header */}
        <div className="page-header">
          <div className="page-header-left" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              className="btn btn-ghost"
              onClick={handleBackToList}
              style={{ padding: '6px 12px' }}
            >
              &larr; Back
            </button>
            <div>
              <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {templateDetail.name}
                <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>
                  v{templateDetail.version}
                </span>
                {templateDetail.is_system && (
                  <span className="badge badge-info" style={{ fontSize: '0.7rem' }}>
                    System
                  </span>
                )}
              </h1>
              <p className="page-subtitle">{templateDetail.slug}</p>
            </div>
          </div>
          <div className="page-header-right">
            {!templateDetail.is_system && !isEditing && (
              <button
                className="btn btn-secondary"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </button>
            )}
            {isEditing && (
              <>
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    setIsEditing(false);
                    // Reset edit data
                    setEditData({
                      name: templateDetail.name,
                      description: templateDetail.description || '',
                      version: templateDetail.version,
                      is_active: templateDetail.is_active,
                    });
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
            <button className="btn btn-ghost" onClick={() => fetchTemplateDetail(selectedSlug!)}>
              <RefreshIcon size={18} />
            </button>
          </div>
        </div>

        {/* System template banner */}
        {templateDetail.is_system && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            marginBottom: '1rem',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'var(--info)',
          }}>
            <AlertIcon size={16} />
            System templates cannot be modified. Create a custom template to customize.
          </div>
        )}

        {/* Content: two columns */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1rem',
        }}>
          {/* Left: Metadata */}
          <div className="glass-card" style={{ padding: '1.25rem' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '15px' }}>
              Template Metadata
            </h3>

            {isEditing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                    Name
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                    Description
                  </label>
                  <textarea
                    className="input"
                    rows={3}
                    value={editData.description}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    style={{ resize: 'vertical' }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500 }}>
                    Version
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={editData.version}
                    onChange={(e) => setEditData({ ...editData, version: e.target.value })}
                    placeholder="1.0.0"
                  />
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'block', marginBottom: '2px' }}>
                    Name
                  </span>
                  <span style={{ fontSize: '14px' }}>{templateDetail.name}</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'block', marginBottom: '2px' }}>
                    Slug
                  </span>
                  <span style={{ fontSize: '14px', fontFamily: 'monospace' }}>{templateDetail.slug}</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'block', marginBottom: '2px' }}>
                    Description
                  </span>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {templateDetail.description || 'No description'}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'block', marginBottom: '2px' }}>
                    Version
                  </span>
                  <span style={{ fontSize: '14px' }}>{templateDetail.version}</span>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div>
                    <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'block', marginBottom: '2px' }}>
                      Status
                    </span>
                    <span className={`badge ${templateDetail.is_active ? 'badge-success' : 'badge-warning'}`}>
                      {templateDetail.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'block', marginBottom: '2px' }}>
                      Type
                    </span>
                    <span className={`badge ${templateDetail.is_system ? 'badge-info' : 'badge-secondary'}`}>
                      {templateDetail.is_system ? 'System' : 'Custom'}
                    </span>
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'block', marginBottom: '2px' }}>
                    Navigation
                  </span>
                  <span style={{ fontSize: '13px' }}>
                    {navGroups.length} groups, {navGroups.reduce((s, g) => s + (g.items?.length ?? 0), 0)} items
                  </span>
                </div>
                {templateDetail.feature_defaults && templateDetail.feature_defaults.length > 0 && (
                  <div>
                    <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'block', marginBottom: '4px' }}>
                      Default Features
                    </span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {templateDetail.feature_defaults.map((f, i) => (
                        <span key={i} className="badge badge-secondary" style={{ fontSize: '0.6rem' }}>
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Assigned Tenants note */}
            <div style={{
              marginTop: '20px',
              padding: '12px',
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.06)',
            }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Tenant assignment tracking coming in a future release.
              </span>
            </div>
          </div>

          {/* Right: NavTreePreview */}
          <div className="glass-card" style={{ padding: '1.25rem' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '15px' }}>
              Navigation Structure
            </h3>
            <NavTreePreview
              groups={navGroups}
              stubMetadata={templateDetail.stub_metadata}
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="page-container">
      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: '1rem', background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit' }}>
            Dismiss
          </button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
          <CheckCircleIcon size={16} />
          {successMessage}
        </div>
      )}

      {viewMode === 'list' ? renderListView() : renderDetailView()}
    </div>
  );
};

export default TemplatesPage;
